import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { generatePricePrediction, generateMarketAnalysis, generateStockPrediction } from '../../../services/openaiServices';
import { runDiagnosticTest } from '../../../services/openaiServices';
import { getConfigStatus, getSetupGuide } from '../../../services/openaiClient';

const AIPredictionPanel = ({ activeMarket = 'crypto' }) => {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(activeMarket === 'crypto' ? 'BTC' : 'AAPL');

  // Mock historical data for both crypto and stocks
  const mockHistoricalData = {
    // Crypto data
    BTC: [
      { date: '2025-01-01', price: 65000 },
      { date: '2025-01-02', price: 66200 },
      { date: '2025-01-03', price: 65800 },
      { date: '2025-01-04', price: 67100 },
      { date: '2025-01-05', price: 67420 },
    ],
    ETH: [
      { date: '2025-01-01', price: 3800 },
      { date: '2025-01-02', price: 3920 },
      { date: '2025-01-03', price: 3850 },
      { date: '2025-01-04', price: 4050 },
      { date: '2025-01-05', price: 4100 },
    ],
    // Stock data
    AAPL: [
      { date: '2025-01-01', price: 185.50 },
      { date: '2025-01-02', price: 187.20 },
      { date: '2025-01-03', price: 186.85 },
      { date: '2025-01-04', price: 189.40 },
      { date: '2025-01-05', price: 190.15 },
    ],
    GOOGL: [
      { date: '2025-01-01', price: 142.80 },
      { date: '2025-01-02', price: 144.50 },
      { date: '2025-01-03', price: 143.20 },
      { date: '2025-01-04', price: 146.75 },
      { date: '2025-01-05', price: 147.95 },
    ],
    MSFT: [
      { date: '2025-01-01', price: 420.50 },
      { date: '2025-01-02', price: 422.80 },
      { date: '2025-01-03', price: 419.90 },
      { date: '2025-01-04', price: 425.60 },
      { date: '2025-01-05', price: 427.30 },
    ],
    TSLA: [
      { date: '2025-01-01', price: 245.60 },
      { date: '2025-01-02', price: 248.90 },
      { date: '2025-01-03', price: 244.15 },
      { date: '2025-01-04', price: 252.40 },
      { date: '2025-01-05', price: 255.80 },
    ]
  };

  const assetOptions = {
    crypto: [
      { symbol: 'BTC', name: 'Bitcoin', currentPrice: 67420 },
      { symbol: 'ETH', name: 'Ethereum', currentPrice: 4100 },
      { symbol: 'SOL', name: 'Solana', currentPrice: 145 },
      { symbol: 'ADA', name: 'Cardano', currentPrice: 0.68 },
    ],
    stocks: [
      { symbol: 'AAPL', name: 'Apple Inc.', currentPrice: 190.15 },
      { symbol: 'GOOGL', name: 'Alphabet Inc.', currentPrice: 147.95 },
      { symbol: 'MSFT', name: 'Microsoft Corp.', currentPrice: 427.30 },
      { symbol: 'TSLA', name: 'Tesla Inc.', currentPrice: 255.80 },
      { symbol: 'AMZN', name: 'Amazon.com Inc.', currentPrice: 172.45 },
      { symbol: 'NVDA', name: 'NVIDIA Corp.', currentPrice: 890.25 },
    ]
  };

  // Update selected asset when market changes
  useEffect(() => {
    if (activeMarket === 'crypto' && !assetOptions?.crypto?.find(a => a?.symbol === selectedAsset)) {
      setSelectedAsset('BTC');
    } else if (activeMarket === 'stocks' && !assetOptions?.stocks?.find(a => a?.symbol === selectedAsset)) {
      setSelectedAsset('AAPL');
    }
  }, [activeMarket, selectedAsset]);

  // Enhanced diagnostic function with comprehensive validation and reporting
  const runDiagnostics = async () => {
    setLoading(true);
    setError(null);

    try {
      console.group('🔧 Enhanced OpenAI Configuration Diagnostics');
      
      // Enhanced local validation
      const localValidation = validateAndDiagnoseAPIKey();
      console.log('Local Validation:', localValidation);
      
      // Get configuration status
      const configStatus = getConfigStatus();
      console.log('Configuration Status:', configStatus);
      
      // Get setup guide
      const setupGuide = getSetupGuide();
      console.log('Setup Guide:', setupGuide);
      
      // Run authentication test
      const diagnosticResult = await runDiagnosticTest();
      console.log('Authentication Test:', diagnosticResult);
      
      console.groupEnd();

      if (diagnosticResult?.success) {
        setError({
          type: 'DIAGNOSTICS_SUCCESS',
          message: 'OpenAI API configuration verified and working!',
          action: `✅ Authentication successful - API ready for use`,
          suggestion: `All AI features are now available for ${activeMarket} analysis`,
          severity: 'success',
          details: {
            status: configStatus?.status,
            modelsAvailable: diagnosticResult?.details?.modelsAvailable || 'Available',
            testTime: diagnosticResult?.timestamp,
            connectionTime: diagnosticResult?.details?.connectionTime
          },
          quickActions: [
            {
              label: 'Generate Prediction',
              action: handleGeneratePrediction,
              icon: 'TrendingUp'
            },
            {
              label: 'Market Analysis',
              action: handleGenerateAnalysis,
              icon: 'BarChart3'
            }
          ]
        });
      } else {
        // Enhanced error display with comprehensive guidance
        const troubleshooting = diagnosticResult?.details?.suggestions || 
                              configStatus?.troubleshooting?.quickFixes || 
                              localValidation?.issues?.map(issue => issue?.solution) || [];
        
        setError({
          type: 'DIAGNOSTICS_FAILED',
          message: 'OpenAI API configuration issue detected',
          action: diagnosticResult?.message || 'Authentication failed',
          suggestion: diagnosticResult?.details?.quickFix || 'Check your API key configuration',
          severity: 'error',
          troubleshooting,
          setupGuide: configStatus?.status !== 'ready' ? setupGuide : null,
          details: {
            localValidation: localValidation,
            keyFound: configStatus?.hasApiKey,
            keyValid: configStatus?.isValidFormat,
            clientReady: configStatus?.clientReady,
            errorType: diagnosticResult?.details?.error || 'configuration_invalid',
            issues: localValidation?.issues
          },
          quickActions: [
            {
              label: 'Get API Key',
              action: () => window.open('https://platform.openai.com/api-keys', '_blank'),
              icon: 'ExternalLink'
            },
            {
              label: 'Check Billing',
              action: () => window.open('https://platform.openai.com/account/billing', '_blank'),
              icon: 'CreditCard'
            },
            {
              label: 'Retry Test',
              action: runDiagnostics,
              icon: 'RefreshCw'
            }
          ]
        });
      }
    } catch (error) {
      console.error('Enhanced diagnostic test failed:', error);
      setError({
        type: 'DIAGNOSTICS_ERROR',
        message: 'Unable to run comprehensive diagnostic test',
        action: 'An error occurred while testing the OpenAI configuration',
        suggestion: 'Check the browser console for more details',
        severity: 'error',
        quickActions: [
          {
            label: 'Retry',
            action: runDiagnostics,
            icon: 'RefreshCw'
          },
          {
            label: 'Manual Setup',
            action: () => window.open('https://platform.openai.com/docs/quickstart', '_blank'),
            icon: 'Book'
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  // Enhanced API key validation and diagnostic workflow
  const validateAndDiagnoseAPIKey = () => {
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    
    // Comprehensive validation checks
    const issues = [];
    
    if (!apiKey || apiKey?.trim()?.length === 0) {
      issues?.push({
        type: 'MISSING_KEY',
        message: 'OpenAI API key not found in environment variables',
        solution: 'Add VITE_OPENAI_API_KEY to your .env file'
      });
    } else if (apiKey === 'your-openai-api-key-here' || 
               apiKey === 'undefined'|| apiKey?.includes('your-') ||
               apiKey?.includes('xxxxx')) {
      issues?.push({
        type: 'PLACEHOLDER_KEY',
        message: 'OpenAI API key is still a placeholder',
        solution: 'Replace with your actual OpenAI API key'
      });
    } else if (!apiKey?.startsWith('sk-')) {
      issues?.push({
        type: 'INVALID_FORMAT',
        message: 'OpenAI API key format is invalid',
        solution: 'API key must start with "sk-"'
      });
    } else if (apiKey?.length < 50) {
      issues?.push({
        type: 'INVALID_LENGTH',
        message: 'OpenAI API key appears too short',
        solution: 'Verify the complete key was copied'
      });
    }

    // Check for common formatting issues
    if (apiKey && (apiKey?.includes(' ') || apiKey?.includes('\n') || apiKey?.includes('\t'))) {
      issues?.push({
        type: 'FORMATTING_ERROR',
        message: 'API key contains whitespace characters',
        solution: 'Remove any spaces, tabs, or line breaks from the key'
      });
    }

    return {
      isValid: issues?.length === 0,
      issues,
      apiKeyFound: !!apiKey,
      apiKeyPrefix: apiKey?.substring(0, 8) || 'Not found'
    };
  };

  const handleGeneratePrediction = async () => {
    // Enhanced validation with detailed diagnostics
    const validation = validateAndDiagnoseAPIKey();
    
    if (!validation?.isValid) {
      const primaryIssue = validation?.issues?.[0];
      setError({
        type: primaryIssue?.type || 'CONFIGURATION_ERROR',
        message: 'OpenAI API configuration issue detected',
        action: primaryIssue?.message || 'API key validation failed',
        suggestion: primaryIssue?.solution || 'Check your API key configuration',
        severity: 'error',
        diagnostics: {
          keyFound: validation?.apiKeyFound,
          keyPrefix: validation?.apiKeyPrefix,
          totalIssues: validation?.issues?.length,
          allIssues: validation?.issues
        },
        quickActions: [
          {
            label: 'Get API Key',
            action: () => window.open('https://platform.openai.com/api-keys', '_blank'),
            icon: 'ExternalLink'
          },
          {
            label: 'Run Diagnostics',
            action: runDiagnostics,
            icon: 'Settings'
          }
        ]
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const historicalData = mockHistoricalData?.[selectedAsset] || mockHistoricalData?.BTC;
      let prediction;

      if (activeMarket === 'crypto') {
        prediction = await generatePricePrediction(selectedAsset, historicalData);
      } else {
        prediction = await generateStockPrediction(selectedAsset, historicalData);
      }
      
      // Handle both successful and fallback responses with enhanced messaging
      if (prediction) {
        setPredictions(prev => [
          {
            id: Date.now(),
            ...prediction,
            assetType: activeMarket,
            timestamp: new Date()?.toISOString(),
          },
          ...prev?.slice(0, 4), // Keep only the 5 most recent predictions
        ]);

        // Show specific messaging for fallback predictions with better context
        if (prediction?.fallback) {
          const reason = prediction?.reason || prediction?.errorMessage || 'Unknown reason';
          setError({
            type: 'API_LIMITATION',
            message: `${prediction?.quickNote || 'Using fallback prediction data'}`,
            action: `Reason: ${reason}`,
            suggestion: 'Run diagnostics to test API connection and resolve issues',
            severity: 'warning',
            quickActions: [
              {
                label: 'Test Connection',
                action: runDiagnostics,
                icon: 'Wifi'
              },
              {
                label: 'Check API Key',
                action: () => window.open('https://platform.openai.com/api-keys', '_blank'),
                icon: 'ExternalLink'
              }
            ]
          });
        } else {
          // Show success for real API responses
          setError({
            type: 'SUCCESS',
            message: `AI prediction generated successfully for ${selectedAsset}`,
            action: 'Real-time analysis completed using OpenAI GPT-4',
            severity: 'success'
          });
        }
      } else {
        throw new Error('No prediction data received from service');
      }
    } catch (err) {
      console.error('Error generating prediction:', err?.message || err);
      
      // Enhanced error handling with specific authentication failure detection and better messaging
      let errorType = 'GENERAL_ERROR';
      let message = 'Failed to generate AI prediction';
      let action = err?.message || 'An unexpected error occurred';
      let suggestion = 'Try again or contact support if the issue persists';
      let quickActions = [
        {
          label: 'Retry',
          action: handleGeneratePrediction,
          icon: 'RefreshCw'
        },
        {
          label: 'Diagnostics',
          action: runDiagnostics,
          icon: 'Settings'
        }
      ];

      if (err?.status === 401 || err?.code === 'AUTHENTICATION_ERROR' || err?.authenticationFailed) {
        errorType = 'AUTHENTICATION_ERROR';
        message = 'OpenAI API authentication failed';
        action = 'Your API key may be invalid, expired, or revoked';
        suggestion = 'Visit https://platform.openai.com/api-keys to verify your key';
        quickActions = [
          {
            label: 'Check API Key',
            action: () => window.open('https://platform.openai.com/api-keys', '_blank'),
            icon: 'ExternalLink'
          },
          {
            label: 'Check Billing',
            action: () => window.open('https://platform.openai.com/account/billing', '_blank'),
            icon: 'CreditCard'
          },
          {
            label: 'Run Diagnostics',
            action: runDiagnostics,
            icon: 'Settings'
          }
        ];
      } else if (err?.status === 429 || err?.code === 'RATE_LIMIT_EXCEEDED') {
        errorType = 'RATE_LIMIT_ERROR';
        message = 'OpenAI API quota exceeded';
        action = 'Rate limit reached or quota exhausted';
        suggestion = 'Visit https://platform.openai.com/account/billing to check usage';
        quickActions = [
          {
            label: 'Check Usage',
            action: () => window.open('https://platform.openai.com/account/usage', '_blank'),
            icon: 'BarChart3'
          },
          {
            label: 'Upgrade Plan',
            action: () => window.open('https://platform.openai.com/account/billing', '_blank'),
            icon: 'ArrowUp'
          }
        ];
      } else if (err?.code === 'NETWORK_ERROR' || err?.message?.includes('network')) {
        errorType = 'NETWORK_ERROR';
        message = 'Network connection issue';
        action = 'Unable to reach OpenAI API servers';
        suggestion = 'Check your internet connection and firewall settings';
        quickActions = [
          {
            label: 'Retry',
            action: handleGeneratePrediction,
            icon: 'RefreshCw'
          },
          {
            label: 'Test Connection',
            action: runDiagnostics,
            icon: 'Wifi'
          }
        ];
      }

      setError({
        type: errorType,
        message,
        action,
        suggestion,
        severity: 'error',
        quickActions
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAnalysis = async () => {
    // Enhanced validation with detailed diagnostics  
    const validation = validateAndDiagnoseAPIKey();
    
    if (!validation?.isValid) {
      const primaryIssue = validation?.issues?.[0];
      setError({
        type: primaryIssue?.type || 'CONFIGURATION_ERROR',
        message: 'OpenAI API configuration issue detected',
        action: primaryIssue?.message || 'API key validation failed',
        suggestion: primaryIssue?.solution || 'Check your API key configuration',
        severity: 'error',
        diagnostics: validation,
        quickActions: [
          {
            label: 'Get API Key',
            action: () => window.open('https://platform.openai.com/api-keys', '_blank'),
            icon: 'ExternalLink'
          },
          {
            label: 'Run Diagnostics',
            action: runDiagnostics,
            icon: 'Settings'
          }
        ]
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const currentAssets = assetOptions?.[activeMarket] || assetOptions?.crypto;
      const selectedAssetData = currentAssets?.find(a => a?.symbol === selectedAsset);
      const marketData = {
        currentPrice: selectedAssetData?.currentPrice,
        change24h: Math.random() * 10 - 5, // Mock 24h change
        volume24h: Math.random() * 1000000000, // Mock volume
        marketCap: selectedAssetData?.currentPrice * (activeMarket === 'crypto' ? 21000000 : 1000000000), // Mock market cap
      };

      const analysis = await generateMarketAnalysis(selectedAsset, marketData, activeMarket);
      
      if (analysis) {
        console.log('Generated Analysis:', analysis);
        
        // Enhanced success/fallback messaging with better context
        if (analysis?.fallback) {
          const reason = analysis?.reason || analysis?.errorMessage || 'Unknown reason';
          setError({
            type: 'API_LIMITATION',
            message: `Market analysis generated using fallback data for ${selectedAsset}`,
            action: `Reason: ${reason}`,
            suggestion: 'Run diagnostics to enable full AI analysis features',
            severity: 'warning',
            quickActions: [
              {
                label: 'Test API',
                action: runDiagnostics,
                icon: 'Wifi'
              },
              {
                label: 'Check Configuration',
                action: () => window.open('https://platform.openai.com/api-keys', '_blank'),
                icon: 'Settings'
              }
            ]
          });
        } else {
          setError({
            type: 'SUCCESS',
            message: `AI market analysis generated for ${selectedAsset}`,
            action: 'Analysis generated using OpenAI GPT-4 and logged to console',
            severity: 'success'
          });
        }
      } else {
        throw new Error('No analysis data received from service');
      }
    } catch (err) {
      console.error('Error generating analysis:', err?.message || err);
      
      // Enhanced error handling matching the prediction function patterns
      let errorType = 'GENERAL_ERROR';
      let message = 'Failed to generate market analysis';
      let action = err?.message || 'An unexpected error occurred';
      let suggestion = 'Try again or check your API configuration';
      let quickActions = [
        {
          label: 'Retry',
          action: handleGenerateAnalysis,
          icon: 'RefreshCw'
        },
        {
          label: 'Diagnostics',
          action: runDiagnostics,
          icon: 'Settings'
        }
      ];

      if (err?.status === 401 || err?.code === 'AUTHENTICATION_ERROR') {
        errorType = 'AUTHENTICATION_ERROR';
        message = 'OpenAI API authentication failed';
        action = 'Your API key may be invalid, expired, or revoked';
        suggestion = 'Check your API key at https://platform.openai.com/api-keys';
        quickActions = [
          {
            label: 'Check API Key',
            action: () => window.open('https://platform.openai.com/api-keys', '_blank'),
            icon: 'ExternalLink'
          },
          {
            label: 'Run Diagnostics',
            action: runDiagnostics,
            icon: 'Settings'
          }
        ];
      } else if (err?.status === 429) {
        errorType = 'RATE_LIMIT_ERROR';
        message = 'OpenAI API quota exceeded';
        action = 'Rate limit reached or quota exhausted';
        suggestion = 'Check billing and usage at OpenAI dashboard';
        quickActions = [
          {
            label: 'Check Usage',
            action: () => window.open('https://platform.openai.com/account/usage', '_blank'),
            icon: 'BarChart3'
          },
          {
            label: 'Upgrade Plan',
            action: () => window.open('https://platform.openai.com/account/billing', '_blank'),
            icon: 'ArrowUp'
          }
        ];
      }

      setError({
        type: errorType,
        message,
        action,
        suggestion,
        severity: 'error',
        quickActions
      });
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'Low': return 'text-success';
      case 'Medium': return 'text-warning';
      case 'High': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'Bullish': return 'text-success';
      case 'Bearish': return 'text-destructive';
      case 'Neutral': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const currentAssets = assetOptions?.[activeMarket] || assetOptions?.crypto;

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Icon name="Brain" size={24} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">AI Predictions</h3>
            <p className="text-sm text-muted-foreground">
              {activeMarket === 'crypto' ? 'Crypto' : 'Stock'} predictions powered by OpenAI GPT-4
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={selectedAsset}
            onChange={(e) => setSelectedAsset(e?.target?.value)}
            className="px-3 py-1 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            {currentAssets?.map((asset) => (
              <option key={asset?.symbol} value={asset?.symbol}>
                {asset?.name} ({asset?.symbol})
              </option>
            ))}
          </select>
          {/* Add diagnostics button */}
          <Button
            onClick={runDiagnostics}
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            title="Run OpenAI configuration diagnostics"
            iconName="Settings"
          />
        </div>
      </div>
      {/* Enhanced error display with comprehensive guidance */}
      {error && (
        <div className={`mb-4 p-4 rounded-lg border ${
          error?.severity === 'success' ? 'bg-success/10 border-success/20' :
          error?.severity === 'warning'? 'bg-warning/10 border-warning/20' : 'bg-destructive/10 border-destructive/20'
        }`}>
          <div className="flex items-start space-x-3">
            <Icon 
              name={
                error?.severity === 'success' ? "CheckCircle" :
                error?.severity === 'warning' ? "AlertTriangle" : "AlertCircle"
              } 
              size={20} 
              className={
                error?.severity === 'success' ? 'text-success mt-0.5' :
                error?.severity === 'warning'? 'text-warning mt-0.5' : 'text-destructive mt-0.5'
              } 
            />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${
                error?.severity === 'success' ? 'text-success' :
                error?.severity === 'warning'? 'text-warning' : 'text-destructive'
              }`}>
                {error?.message || (typeof error === 'string' ? error : 'An error occurred')}
              </p>
              
              {error?.action && (
                <p className="text-sm text-muted-foreground mt-1">
                  {error?.action}
                </p>
              )}
              
              {error?.suggestion && (
                <div className="mt-2">
                  {error?.suggestion?.includes('https://') ? (
                    <a 
                      href={error?.suggestion} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline inline-flex items-center space-x-1"
                    >
                      <span>Visit OpenAI Dashboard</span>
                      <Icon name="ExternalLink" size={14} />
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      {error?.suggestion}
                    </p>
                  )}
                </div>
              )}

              {/* Troubleshooting steps */}
              {error?.troubleshooting && error?.troubleshooting?.length > 0 && (
                <div className="mt-3 p-3 bg-muted/30 rounded text-xs">
                  <p className="font-medium text-foreground mb-2">Troubleshooting Steps:</p>
                  <ul className="space-y-1 text-muted-foreground">
                    {error?.troubleshooting?.map((step, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-primary font-medium">{index + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Setup guide for configuration errors */}
              {error?.setupGuide && (
                <div className="mt-3 p-3 bg-primary/10 border border-primary/20 rounded text-xs">
                  <p className="font-medium text-primary mb-2">Setup Guide:</p>
                  <div className="space-y-2">
                    {error?.setupGuide?.steps?.map((step) => (
                      <div key={step?.step} className="flex items-start space-x-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                          step?.status === 'completed' ? 'bg-success text-white' :
                          step?.status === 'required'? 'bg-destructive text-white' : 'bg-muted text-muted-foreground'
                        }`}>
                          {step?.step}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{step?.title}</p>
                          <p className="text-muted-foreground">{step?.description}</p>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded mt-1 inline-block">
                            {step?.action}
                          </code>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-primary/20">
                    <p className="text-primary font-medium">Helpful Links:</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {Object.entries(error?.setupGuide?.links || {})?.map(([key, url]) => (
                        <a
                          key={key}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline capitalize"
                        >
                          {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Configuration details for debugging */}
              {error?.details && (
                <div className="mt-3 p-2 bg-muted/20 rounded text-xs">
                  <p className="font-medium text-foreground mb-1">Configuration Details:</p>
                  <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                    {Object.entries(error?.details)?.map(([key, value]) => (
                      <div key={key}>
                        <span className="capitalize">{key?.replace(/([A-Z])/g, ' $1')?.trim()}:</span>
                        <span className="ml-1 font-mono">
                          {typeof value === 'boolean' ? (value ? '✅' : '❌') : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick action buttons for common fixes */}
              {error?.quickActions && error?.quickActions?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {error?.quickActions?.map((quickAction, index) => (
                    <Button
                      key={index}
                      onClick={quickAction?.action}
                      variant="outline"
                      size="sm"
                      iconName={quickAction?.icon}
                      className="text-xs"
                    >
                      {quickAction?.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
            <Button
              onClick={() => setError(null)}
              variant="ghost"
              size="sm"
              iconName="X"
              className="text-muted-foreground hover:text-foreground"
            />
          </div>
        </div>
      )}
      <div className="flex space-x-3 mb-6">
        <Button
          onClick={handleGeneratePrediction}
          disabled={loading}
          variant="default"
          size="sm"
          iconName={loading ? "Loader2" : "TrendingUp"}
          className={loading ? "animate-spin" : ""}
        >
          {loading ? 'Generating...' : `Predict ${activeMarket === 'crypto' ? 'Price' : 'Stock'}`}
        </Button>
        <Button
          onClick={handleGenerateAnalysis}
          disabled={loading}
          variant="outline"
          size="sm"
          iconName="BarChart3"
        >
          {activeMarket === 'crypto' ? 'Crypto' : 'Stock'} Analysis
        </Button>
      </div>
      <div className="space-y-4">
        {predictions?.length === 0 ? (
          <div className="text-center py-8">
            <Icon 
              name={activeMarket === 'crypto' ? "Coins" : "TrendingUp"} 
              size={48} 
              className="text-muted-foreground/50 mx-auto mb-3" 
            />
            <p className="text-muted-foreground">No predictions yet</p>
            <p className="text-sm text-muted-foreground">
              Generate your first AI {activeMarket === 'crypto' ? 'crypto' : 'stock'} prediction above
            </p>
          </div>
        ) : (
          predictions
            ?.filter(pred => !pred?.assetType || pred?.assetType === activeMarket)
            ?.map((prediction) => (
              <div key={prediction?.id} className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={prediction?.assetType === 'crypto' ? "Coins" : "TrendingUp"} 
                      size={16} 
                      className="text-muted-foreground" 
                    />
                    <span className="font-semibold text-card-foreground">{prediction?.symbol}</span>
                    <span className="text-sm text-muted-foreground">
                      ${prediction?.currentPrice?.toLocaleString()}
                    </span>
                  </div>
                  <span className={`text-sm font-medium ${getRiskLevelColor(prediction?.riskLevel)}`}>
                    {prediction?.riskLevel} Risk
                  </span>
                </div>

                {prediction?.predictions?.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {prediction?.predictions?.slice(0, 2)?.map((pred, index) => (
                      <div key={index} className="bg-muted/20 rounded p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{pred?.timeframe}</span>
                          <span className={`text-sm font-medium ${getTrendColor(pred?.trend)}`}>
                            {pred?.trend}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-semibold text-card-foreground">
                            ${pred?.priceTarget?.toLocaleString()}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {pred?.confidence}% confidence
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {prediction?.analysis && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {prediction?.analysis}
                  </p>
                )}

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Generated {new Date(prediction?.timestamp)?.toLocaleTimeString()}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ExternalLink"
                    onClick={() => window.location.href = '/ai-predictions-detail'}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            ))
        )}
      </div>
      {predictions?.filter(pred => !pred?.assetType || pred?.assetType === activeMarket)?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <Button
            variant="outline"
            className="w-full"
            iconName="Plus"
            onClick={() => window.location.href = '/ai-predictions-detail'}
          >
            View All {activeMarket === 'crypto' ? 'Crypto' : 'Stock'} Predictions
          </Button>
        </div>
      )}
    </div>
  );
};

export default AIPredictionPanel;